// =============================================================================
// MessageBubble/attachedDocument.ts — Ponte para `attachedDocumentId` (F46-S29).
//
// F46-S11 (este slot) depende de `attachedDocumentId` no `MessageSchema`
// (PR #729, F46-S29) — no momento em que este slot foi implementado, o PR
// estava em revisão, NÃO mergeado em `main`. `apps/web/src/features/
// conversations/types.ts` está FORA de `files_allowed` deste slot (não pode
// ser editado aqui), então o campo não pode ser adicionado à interface
// `Message` por este slot.
//
// Solução: basear-se no contrato JÁ REVISADO do PR #729 (campo
// `attachedDocumentId: string | null`, ver
// apps/api/src/modules/conversations/schemas.ts no branch feat/f46-s29) sem
// duplicar a query nem inventar um campo novo — só uma leitura estrutural
// tolerante a ausência:
//
//   - Antes do merge de F46-S29: o campo não existe no JSON de resposta.
//     `getAttachedDocumentId` devolve `null` (mesmo comportamento de "ainda
//     não virou documento" que o contrato define para `null` explícito).
//   - Depois do merge: o campo passa a vir preenchido de verdade, e esta
//     função já lê o valor real sem qualquer mudança de código aqui.
//
// Quando F46-S29 mergear e `Message` ganhar `attachedDocumentId` oficialmente
// em `types.ts`, este arquivo pode ser simplificado para um alias direto —
// mas nenhuma mudança de runtime é necessária.
// =============================================================================

import type { Message } from '../../types';

/**
 * Id do documento (`documents.id`) ao qual esta mensagem já foi anexada, ou
 * `null` quando ainda não foi (ou nunca será, se `attachedDocumentId` segue
 * ausente do contrato). Nunca lança, nunca faz fetch adicional — lê do
 * objeto `Message` já carregado pelo `useMessages`/socket.
 */
export function getAttachedDocumentId(message: Message): string | null {
  return message.attachedDocumentId ?? null;
}

/**
 * `true` quando a mídia desta mensagem tem arquivo guardado e pode virar
 * documento (F46-S35). Ausência do campo (cache antigo, ou API ainda não
 * atualizada) é tratada como `false` — conservador: nunca oferece uma ação
 * que a API vai recusar com 422 permanente. Nunca lança, nunca faz fetch
 * adicional — lê do objeto `Message` já carregado pelo `useMessages`/socket.
 */
export function getCanAttachAsDocument(message: Message): boolean {
  return message.canAttachAsDocument ?? false;
}
